/**
 * Per-language magnitude — total sends, engagement rate, daily velocity.
 * Shows how outreach is distributed across languages and which are
 * accelerating (compare 30/90/365 day windows).
 */

import React from 'react';
import { useLanguageMagnitude } from '@/hooks/ml';

function fmt(n: number): string {
  return n.toLocaleString();
}

function fmtPct(n: number | null): string {
  return n == null ? '—' : `${n.toFixed(2)}%`;
}

export const LanguageMagnitudeCard: React.FC = () => {
  const { data, isLoading } = useLanguageMagnitude();
  const langs = (data?.languages ?? []).filter((l) => l.sends_12m > 0);

  // Aggregate to (cbo_id-collapsed) when consolidated, otherwise show per-language directly.
  const grouped = (() => {
    const map = new Map<string, typeof langs[0]>();
    for (const r of langs) {
      const cur = map.get(r.message_language);
      if (!cur) {
        map.set(r.message_language, { ...r });
      } else {
        cur.sends_12m += r.sends_12m;
        cur.sends_90d += r.sends_90d;
        cur.sends_30d += r.sends_30d;
        cur.engagements_12m += r.engagements_12m;
        cur.engagements_90d += r.engagements_90d;
        cur.unique_recipients_12m += r.unique_recipients_12m;
      }
    }
    return Array.from(map.values()).sort((a, b) => b.sends_12m - a.sends_12m);
  })();

  const maxSends = grouped[0]?.sends_12m ?? 1;

  // Growth signal: compare daily rate in last 30d vs the trailing 60d before it.
  function growthSignal(r: { sends_30d: number; sends_90d: number }): { pct: number; arrow: string; color: string } {
    const recent = r.sends_30d / 30;
    const earlierTotal = r.sends_90d - r.sends_30d;
    const earlier = earlierTotal / 60;
    if (earlier <= 0) return { pct: 0, arrow: '·', color: 'text-gray-400' };
    const pct = ((recent - earlier) / earlier) * 100;
    if (pct >= 25) return { pct, arrow: '▲', color: 'text-emerald-600' };
    if (pct <= -25) return { pct, arrow: '▼', color: 'text-rose-600' };
    return { pct, arrow: '·', color: 'text-gray-500' };
  }

  return (
    <div>
      <div className="text-xs text-gray-500 mb-3">
        Outreach volume by message language over the last 12 months. Growth arrow
        compares the last 30 days' daily send rate against the trailing 60 days.
      </div>
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : grouped.length === 0 ? (
        <div className="text-sm text-gray-500">No outreach activity to summarize.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
              <tr className="border-b border-gray-200">
                <th className="py-2 pr-3">Language</th>
                <th className="py-2 pr-3 text-right">Sends (12m)</th>
                <th className="py-2 pr-3">Share</th>
                <th className="py-2 pr-3 text-right">Daily (90d)</th>
                <th className="py-2 pr-3 text-right">Engage %</th>
                <th className="py-2 pr-3 text-right">Growth</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((r) => {
                const share = (r.sends_12m / maxSends) * 100;
                const g = growthSignal(r);
                return (
                  <tr key={r.message_language} className="border-b border-gray-100 last:border-0">
                    <td className="py-1.5 pr-3 font-medium text-gray-900">{r.message_language}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{fmt(r.sends_12m)}</td>
                    <td className="py-1.5 pr-3">
                      <div className="h-2 w-32 rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full bg-blue-500" style={{ width: `${share}%` }} />
                      </div>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{(r.sends_90d / 90).toFixed(1)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{fmtPct(r.engagement_rate_12m_pct)}</td>
                    <td className={`py-1.5 pr-3 text-right tabular-nums font-medium ${g.color}`}>
                      {g.arrow} {g.pct === 0 ? '—' : `${g.pct >= 0 ? '+' : ''}${g.pct.toFixed(0)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
